import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  CollapsableCard,
  TagColor as Color,
  CounterBadge,
  Icon,
  Tag,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import {
  ApprovalsFlowFragment,
  ApprovalsGroupFragment,
  ApprovalsRuleConfigurationFragment,
  ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ApprovalFlowForm } from 'modules/approvals/components/ApprovalFlowForm'
import RuleDescription from 'modules/approvals/components/RulesConfiguration/RulesDescription'
import { useUpdateApprovalFlow } from 'modules/approvals/hooks/useApprovalFlows'
import { useDeleteRulesConfiguration } from 'modules/approvals/hooks/useRulesConfigurations'
import type { ApprovalFlowArgs } from 'modules/approvals/lib/approvalFlows'
import { approvalFlowToArgs } from 'modules/approvals/lib/approvalFlows'

interface Props {
  group?: ApprovalsGroupFragment
  rule?: ApprovalsRuleConfigurationFragment
  decision?: ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum
  flow: ApprovalsFlowFragment | null | undefined
  index: number
  dragabble?: boolean
  empty?: boolean
  title: string | null | undefined
  tagLabel?: string
  tagType?: Color
  description?: string | null | undefined
  canEdit?: boolean
  canAddSteps?: boolean
  canDeleteSteps?: boolean
}

export const RuleCard = ({
  group,
  rule,
  decision,
  flow,
  index,
  empty,
  dragabble,
  title,
  description,
  tagLabel,
  tagType,
  canEdit,
  canAddSteps,
  canDeleteSteps,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { updateApprovalFlow, loading } = useUpdateApprovalFlow()
  const { deleteRuleConfiguration, loading: deleteLoading } = useDeleteRulesConfiguration()

  const [open, setOpen] = useState(false)
  const [flowArgs, setFlowArgs] = useState<ApprovalFlowArgs>(approvalFlowToArgs(flow))

  const handleOpen = () => {
    if (empty) {
      return
    }
    setOpen(!open)
  }

  const handleOnChange = (value: ApprovalFlowArgs) => {
    setFlowArgs(value)
  }

  const handleSubmit = async () => {
    if (flow?.id) {
      const result = await updateApprovalFlow(flow.id, flowArgs)
      const handled = handleResult(result, () => undefined)
      if (handled.success) {
        setFlasshMessage(
          i18n.t('settings.approvals.approval_flow.flash_messages.successful_update'),
          'correct'
        )
      } else {
        setFlasshMessage(
          i18n.t('settings.approvals.approval_flow.flash_messages.error_update'),
          'wrong'
        )
      }
    }
  }

  const editRule = () => {
    if (group && rule) {
      navigate(
        navigation.approvals.approvalGroupsCategoryId.rulesConfigurations.edit.fullPath({
          id: group.id,
          category: group.category,
          rules_configuration_id: rule.id,
        })
      )
    }
  }

  const deleteRule = async () => {
    if (rule) {
      const result = await deleteRuleConfiguration(rule.id)
      const handled = handleResult(result, () => undefined)
      if (handled.success) {
        setFlasshMessage(
          i18n.t('settings.approvals.approval_flow.flash_messages.successful_update'),
          'correct'
        )
      } else {
        setFlasshMessage(
          i18n.t('settings.approvals.approval_flow.flash_messages.error_update'),
          'wrong'
        )
      }
    }
  }

  const setFlasshMessage = (message: string, type: 'correct' | 'wrong') => {
    app.setFlash({
      type,
      message,
      time: 2000,
    })
  }

  const icon = open ? 'chevron-up' : 'chevron-down'
  const decisionLabel = () => {
    switch (decision) {
      case 'auto_approve':
        return i18n.t('settings.approvals.approval_rule.decisions.auto_approve')
      case 'custom_approval_flow':
        return i18n.t('settings.approvals.approval_rule.decisions.custom_approval_flow')
      default:
        return ''
    }
  }

  const dragAndDropColor = dragabble ? 'grey800' : 'white'

  return (
    <Box paddingBottom="s16">
      <CollapsableCard
        open={open}
        Card={
          <Box
            flexDirection="row"
            justifyContent="spaceBetween"
            width="full"
            alignItems="center"
            height="s32"
          >
            <Box flexDirection="row" gap="s12">
              <Icon.Medium icon="drag-and-drop" color={dragAndDropColor} />
              <CounterBadge count={index} noCountIcon="ai" />
              <Text size="300" weight="medium" color="grey1000">
                {title}
              </Text>
            </Box>

            <Box flexDirection="row" gap="s12" alignItems="center">
              {tagLabel && <Tag type={tagType ?? 'viridian'}>{tagLabel}</Tag>}
              {rule && (
                <PopoverMenu type="list">
                  <PopoverMenu.Trigger>
                    <Button.Decorative type="secondary" iconName="ellipsis" />
                  </PopoverMenu.Trigger>
                  <PopoverMenu.Content side="bottom" align="center">
                    <PopoverMenu.TouchableOption
                      title={i18n.t('settings.approvals.approval_rule.edit_approval_rule')}
                      onClick={editRule}
                    />
                    <PopoverMenu.TouchableOption
                      title={i18n.t('settings.approvals.approval_rule.delete_approval_rule')}
                      onClick={deleteRule}
                      disabled={deleteLoading}
                      danger
                    />
                  </PopoverMenu.Content>
                </PopoverMenu>
              )}

              <Touchable onClick={handleOpen}>
                <Icon.Medium icon={icon} />
              </Touchable>
            </Box>
          </Box>
        }
        Content={
          <Box>
            {description && (
              <Box paddingBottom="s16">
                <RuleDescription description={description} />
                {decision && (
                  <Box paddingTop="s8">
                    <Text size="200">
                      {i18n.t('settings.approvals.approval_rule.decisions.label') + ', '}
                      <Tag type="malibu" inline>
                        {decisionLabel()}
                      </Tag>
                    </Text>
                  </Box>
                )}
              </Box>
            )}

            {flow && (
              <ApprovalFlowForm
                showBorder
                approvalFlow={flowArgs}
                canEdit={!!canEdit}
                canAddSteps={!!canAddSteps}
                canDeleteSteps={!!canDeleteSteps}
                onChange={(value) => {
                  handleOnChange(value)
                }}
              />
            )}
            {canEdit && (
              <Box paddingTop="s24" flexDirection="row" justifyContent="flexEnd">
                <Button onClick={handleSubmit} disabled={loading}>
                  {i18n.t('settings.approvals.approval_group.save_changes')}
                </Button>
              </Box>
            )}
          </Box>
        }
      />
    </Box>
  )
}
